import type { ApplicationConfig } from '@angular/core'
import { ErrorHandler, Injector, inject, provideAppInitializer } from '@angular/core'
import { provideRouter, withPreloading, PreloadAllModules, RouteReuseStrategy } from '@angular/router'
import { provideHttpClient, withInterceptorsFromDi, withXhr, HTTP_INTERCEPTORS } from '@angular/common/http'
import { provideAnimations } from '@angular/platform-browser/animations'
import { routes } from './app.routes'
import { ApiClient } from '@awork/_shared/services/api-client/ApiClient'
import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser/ngx'
import { RequestInterceptor } from './shared/services/interceptors/RequestInterceptor'
import { ErrorInterceptor } from './shared/services/interceptors/ErrorInterceptor'
import { createErrorHandler } from '@sentry/angular'
import { RouteReusableStrategy } from './shared/classes/route-reusable-strategy'
import { installNativePasskeys } from './auth/functions/install-native-passkeys'
import { fetchTranslation } from './shared/functions/translation'
import { MobileUpdateService } from './shared/services/mobile-update-service/mobile-update.service'
import { CriticalUpdateService } from './shared/services/critical-update-service/critical-update.service'
import { ThemeService } from '@awork/_shared/services/theme-service/theme.service'
import { MobileThemeService } from './shared/services/mobile-theme-service/mobile-theme.service'
import { NOTIFICATION_NAVIGATION_ACTIONS } from './framework/services/notification-navigation-service/notification-navigation-actions'
import { NotificationSheetService } from './framework/services/notification-sheet-service/notification-sheet.service'
import { MobileLogoutService } from './framework/services/mobile-logout-service/mobile-logout.service'
import { getProductConfiguration, PRODUCT_CONFIGURATION } from '@awork/_shared/services/product/product-configuration'

export const appConfig: ApplicationConfig = {
  providers: [
    // The mobile app is awork only for now, whatever the host.
    { provide: PRODUCT_CONFIGURATION, useValue: getProductConfiguration('awork') },
    provideRouter(routes, withPreloading(PreloadAllModules)),
    provideHttpClient(withInterceptorsFromDi(), withXhr()),
    provideAnimations(),
    ApiClient,
    InAppBrowser,
    {
      provide: NOTIFICATION_NAVIGATION_ACTIONS,
      useFactory: () => {
        const injector = inject(Injector)

        return {
          showInbox: () => injector.get(NotificationSheetService).show(),
          logout: () => injector.get(MobileLogoutService).logout()
        }
      }
    },
    // Shared comment services read ThemeService, but mobile owns the document theme.
    { provide: ThemeService, useClass: MobileThemeService },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RequestInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true
    },
    provideAppInitializer(installNativePasskeys),
    provideAppInitializer(() => {
      // Create the critical update service first, so it receives every updater event.
      void inject(CriticalUpdateService).initialize()
      void inject(MobileUpdateService).initialize()
    }),
    provideAppInitializer(() => {
      const initializerFn = fetchTranslation()
      return initializerFn()
    }),
    {
      provide: ErrorHandler,
      useValue: createErrorHandler()
    },
    {
      provide: RouteReuseStrategy,
      useClass: RouteReusableStrategy
    }
  ]
}
